'use client';
import { Skeleton, Switch, TextInput } from '@mantine/core';
import { InvitedUser, User, UserStatus, UserTypes } from '@scaleup/models';

import {
  IconBriefcase,
  IconEdit,
  IconExclamationCircleFilled,
  IconMail,
  IconPhone,
  IconPlus,
  IconTrash,
  IconUserFilled,
  IconUserPlus,
  IconUsers,
} from '@tabler/icons-react';
import { FC, useEffect, useState } from 'react';
import {
  useCreateUserInvitesMutation,
  useGetUserQuery,
  useGetUsersQuery,
  useUpdateUserMutation,
} from '../../api';
import { useAlert, useConfirmSwitch, useToggle } from '../../hooks';
import { Button, Modal, Tabs } from '../../ui';
import { validateEmail } from '../../utils';

const ColoredIcon = ({
  iconBackgroundClass,
  iconColor,
  Icon,
}: {
  iconBackgroundClass: string;
  iconColor: string;
  Icon: FC<any>;
}) => {
  return (
    <div
      className={`${iconBackgroundClass} rounded-full w-[40px] h-[40px] flex items-center justify-center`}
    >
      <Icon size={24} color={iconColor} />
    </div>
  );
};

const UserInfo = () => {
  const { data: user, isLoading } = useGetUserQuery();
  const [editMode, toggleEditMode] = useToggle();
  const [fields, setFields] = useState<Partial<User>>({});

  const [updateUser, { isLoading: updateUserLoading }] =
    useUpdateUserMutation();
  const onFieldChange = (field: string, value: string) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!user) {
      return;
    }

    setFields({ ...user });
  }, [user]);

  const onSaveField = async () => {
    const { title, firstName, lastName, phone, userEmail } = fields;
    await updateUser({ title, firstName, lastName, phone, userEmail });
    toggleEditMode();
  };

  if (isLoading || !user) {
    return <Skeleton className="rounded-[2rem]" height={250} />;
  }

  return (
    <div className="bg-white rounded-[2rem] p-8">
      <p className="text-xl font-bold pb-4">Basic Information</p>
      <div className="bg-[#f1f3ff] w-full rounded-[2rem] p-8">
        {editMode ? (
          <div className="flex justify-between">
            <div className="flex gap-4">
              <ColoredIcon
                iconBackgroundClass="bg-[#dce1ff]"
                iconColor="#7189ff"
                Icon={IconUserFilled}
              />

              <div className="w-[500px]">
                <div className="flex gap-4 w-full pb-2">
                  <div className="w-full">
                    <TextInput
                      classNames={{ input: 'rounded-[1rem]' }}
                      label="First Name"
                      value={fields.firstName}
                      onChange={(e) =>
                        onFieldChange('firstName', e.currentTarget.value)
                      }
                    />
                  </div>

                  <div className="w-full">
                    <TextInput
                      classNames={{ input: 'rounded-[1rem] w-full' }}
                      label="Last Name"
                      value={fields.lastName}
                      onChange={(e) =>
                        onFieldChange('lastName', e.currentTarget.value)
                      }
                    />
                  </div>
                </div>

                <div className="py-2">
                  <TextInput
                    classNames={{ input: 'rounded-[1rem] w-full py-2' }}
                    label="Title"
                    value={fields.title}
                    onChange={(e) =>
                      onFieldChange('title', e.currentTarget.value)
                    }
                  />
                </div>

                <div className="py-2">
                  <TextInput
                    classNames={{ input: 'rounded-[1rem] w-full' }}
                    label="Email"
                    disabled
                    value={fields.userEmail}
                    error={
                      validateEmail(fields.userEmail)
                        ? null
                        : 'Please provide a valid email'
                    }
                    onChange={(e) =>
                      onFieldChange('userEmail', e.currentTarget.value)
                    }
                  />
                </div>

                <div className="py-2">
                  <TextInput
                    classNames={{ input: 'rounded-[1rem] w-[500px] py-2' }}
                    label="Phone"
                    value={fields?.phone}
                    onChange={(e) =>
                      onFieldChange('phone', e.currentTarget.value)
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex gap-2">
                <Button
                  text="Cancel"
                  slim
                  variant="secondary"
                  onClick={() => toggleEditMode()}
                />
                <Button
                  text="Save"
                  slim
                  loading={updateUserLoading}
                  variant="primary"
                  onClick={() => onSaveField()}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ColoredIcon
                  iconBackgroundClass="bg-[#dce1ff]"
                  iconColor="#7189ff"
                  Icon={IconUserFilled}
                />

                <span className="text-lg font-bold">
                  {user.firstName} {user.lastName}
                </span>
              </div>

              <button onClick={() => toggleEditMode()}>
                <IconEdit size={24} color="#98a1b4" />
              </button>
            </div>

            <div className="pt-2">
              <div className="flex gap-2 px-12 py-1">
                <IconBriefcase size={24} color="#7189ff" />
                <span>{user.title}</span>
              </div>

              <div className="flex gap-2 px-12 py-1">
                <IconMail size={24} color="#7189ff" />
                <span>{user.userEmail}</span>
              </div>

              {user.phone && (
                <div className="flex gap-2 px-12 py-1">
                  <IconPhone size={24} color="#7189ff" />
                  <span>{user.phone}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface OnConfirmProps {
  title: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  body: string | JSX.Element;
}

const useConfirmationModal = () => {
  const [show, toggle] = useToggle(false);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<OnConfirmProps>();

  const showConfirmationModal = (props: OnConfirmProps) => {
    setDetails(props);
    toggle();
  };

  const closeModal = () => {
    if (details) {
      details.onCancel();
    }
    setDetails(undefined);
    toggle();
  };

  const handleConfirm = async () => {
    if (details) {
      setLoading(true);
      await details.onConfirm();
      toggle();
    }
    setLoading(false);
  };

  const renderConfirmationModal = () => {
    if (show && details) {
      return (
        <Modal
          fullScreen={false}
          title={details.title}
          icon={<IconExclamationCircleFilled color="red" />}
          iconSize={48}
          iconBackground={true}
          iconRounded
          showModal={show}
          handleCloseModal={closeModal}
        >
          <span className="text-text-secondary text-md -mt-4">
            {details.body}
          </span>

          <div className="flex items-center justify-end gap-4 pt-8">
            <div className="w-[124px]">
              <Button text="Cancel" variant="secondary" onClick={closeModal} />
            </div>

            <div className="w-[124px]">
              <Button
                text="Confirm"
                variant="primary"
                loading={loading}
                onClick={() => handleConfirm()}
              />
            </div>
          </div>
        </Modal>
      );
    }
  };

  return { showConfirmationModal, renderConfirmationModal };
};

interface InviteProps {
  onChange: (idx: number, val: Partial<InvitedUser>) => void;
  index: number;
  onRemove: (idx: number) => void;
  hide: boolean;
}

const Invite = ({ index, onChange, onRemove, hide }: InviteProps) => {
  const [userType, setUserType] = useState(UserTypes.member);
  const [email, setEmail] = useState<string>();

  if (hide) {
    return null;
  }

  return (
    <div className="flex items-center justify-between w-full gap-4 h-[64px] my-2">
      <div className="w-6/12">
        <TextInput
          classNames={{ input: 'rounded-[1rem]' }}
          type="email"
          label="Email"
          error={
            email &&
            (validateEmail(email) ? null : 'Please provide a valid email')
          }
          placeholder="user@joinscaleup.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            onChange(index, { userEmail: e.target.value });
          }}
        />
      </div>

      <div
        style={{ paddingTop: '1rem' }}
        className="flex flex-col justify-center h-[64px] w-4/12"
      >
        <Switch
          labelPosition="left"
          color="#89b89f"
          label={USER_TYPE_MAP[userType]}
          onChange={(event) => {
            setUserType(
              event.target.checked ? UserTypes.admin : UserTypes.member
            );
            onChange(index, {
              userType: event.target.checked
                ? UserTypes.admin
                : UserTypes.member,
            });
          }}
        />
      </div>

      <div className="w-2/12">
        {index > 0 && (
          <IconTrash
            className="cursor-pointer"
            color="red"
            onClick={() => {
              onRemove(index);
            }}
          />
        )}
      </div>
    </div>
  );
};

const emptyInvite = {
  invite: { userEmail: '', userType: UserTypes.member },
  valid: false,
};
const defaultInvites = [emptyInvite];

const useInviteModal = () => {
  const [show, toggle] = useToggle(false);
  const [isValid, setIsValid] = useState(false);
  const setAlert = useAlert();
  const [createInvites, { isLoading }] = useCreateUserInvitesMutation();
  const [invites, setInvites] =
    useState<({ invite: InvitedUser; valid: boolean } | undefined)[]>(
      defaultInvites
    );

  useEffect(() => {
    const valid = invites
      .filter((it) => it !== undefined)
      .every((it) => it.valid);

    setIsValid(valid);
  }, [invites]);

  const showInviteModal = () => {
    toggle();
  };

  const closeModal = () => {
    toggle();
    setInvites(defaultInvites);
  };

  const handleConfirm = async () => {
    const { error } = await createInvites(
      invites
        .filter((it) => it !== undefined)
        .filter((it) => it.valid)
        .map((it) => it.invite)
    );

    if (!error) {
      setAlert({
        title: 'Invites sent',
        severity: 'success',
        show: true,
      });
      toggle();
      return;
    }

    setAlert({
      title: 'Failed to send invites',
      severity: 'error',
      show: true,
    });
  };

  const removeInvite = (idx: number) => {
    invites[idx] = undefined;

    setInvites([...invites]);
  };

  const setInvite = (idx: number, val: Partial<InvitedUser>) => {
    const item = invites[idx];
    if (item) {
      const { invite } = item;
      const updated = { ...invite, ...val };
      const valid = validateEmail(updated.userEmail);
      invites[idx] = { invite: updated, valid };
      setInvites([...invites]);
    }

    // invites[idx] = { userEmail: '', userType: UserTypes.member, ...val };
    // setInvites(invites);
  };

  const renderInviteModal = () => {
    if (!show) {
      return null;
    }

    return (
      <Modal
        fullScreen={false}
        title={'Invite User'}
        icon={<IconUsers color="#4298b0" />}
        iconSize={48}
        iconBackground={true}
        iconRounded
        showModal={show}
        handleCloseModal={closeModal}
      >
        <div>
          <span className="text-text-secondary text-md -mt-4 pb-2">
            Invite users to the Scaleup platform via email and assign
            permissions in advance.
          </span>

          <div className="py-2">
            {invites.map((invite, idx) => (
              <Invite
                index={idx}
                onChange={setInvite}
                onRemove={removeInvite}
                hide={!Boolean(invite)}
              />
            ))}
          </div>

          <div style={{ marginLeft: '-32px' }} className="w-[250px]">
            <Button
              text="Invite Additional User"
              variant="link"
              className="w-[250px]"
              prefixIcon={<IconPlus size={20} color="#4298b0" />}
              onClick={() => {
                setInvites([...invites, emptyInvite]);
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-4 pt-8 w-full">
            <div className="w-[124px]">
              <Button text="Cancel" variant="secondary" onClick={closeModal} />
            </div>
            <div className="w-[124px]">
              <Button
                text="Send Invite"
                variant={isValid ? 'primary' : 'disabled'}
                loading={isLoading}
                onClick={() => handleConfirm()}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  return { showInviteModal, renderInviteModal };
};

const USER_TYPE_MAP: Record<UserTypes, string> = {
  [UserTypes.admin]: 'Admin',
  [UserTypes.member]: 'Non-Admin',
};

const USER_STATUS_MAP: Record<UserStatus, string> = {
  [UserStatus.active]: 'Active',
  [UserStatus.inactive]: 'Inactive',
};

const USER_STATUS_TO_ACTION_MAP: Record<UserStatus, string> = {
  [UserStatus.active]: 'activate',
  [UserStatus.inactive]: 'deactivate',
};

const getIsAdmin = (user: User) => {
  return user.userType === UserTypes.admin;
};

const getIsActive = (user: User) => {
  return user.userStatus === UserStatus.active;
};

const PlatformUser = ({
  user,
  isCurrentUser,
}: {
  user: User;
  isCurrentUser: boolean;
}) => {
  const { showConfirmationModal, renderConfirmationModal } =
    useConfirmationModal();

  const [adminChecked, cancelAdmin, confirmAdmin, setAdminChecked] =
    useConfirmSwitch(getIsAdmin(user));
  const [activeChecked, cancelActive, confirmActive, setActiveChecked] =
    useConfirmSwitch(getIsActive(user));

  const [updateUser] = useUpdateUserMutation();

  const onUpdateUser = async (userStatus: UserStatus, userType: UserTypes) => {
    const data = { ...user, userStatus, userType };
    await updateUser(data);
  };

  const showPermissionsUpdateModal = (newType: UserTypes) => {
    showConfirmationModal({
      title: 'Confirm Permissions Update',
      body: (
        <>
          Please confirm a permissions update for{' '}
          <strong>
            {user.firstName} {user.lastName}
          </strong>{' '}
          from {USER_TYPE_MAP[user.userType]} to {USER_TYPE_MAP[newType]}.
        </>
      ),
      onCancel: () => {
        cancelAdmin();
      },
      onConfirm: async () => {
        await onUpdateUser(user.userStatus, newType);
        confirmAdmin();
      },
    });
  };

  const showAccountUpdateModal = (newStatus: UserStatus) => {
    showConfirmationModal({
      title: 'Confirm Account Update',
      body: (
        <>
          Are you sure you want to {USER_STATUS_TO_ACTION_MAP[newStatus]}{' '}
          <strong>
            {user.firstName} {user.lastName}’s
          </strong>{' '}
          ScaleUp Account?
          <br />
          <br />
          Caution: {user.firstName} {user.lastName} will{' '}
          {USER_STATUS_TO_ACTION_MAP[newStatus] === 'activate'
            ? 'now'
            : 'no longer'}{' '}
          have access to the Scaleup Platform.
        </>
      ),
      onCancel: () => {
        cancelActive();
      },
      onConfirm: async () => {
        await onUpdateUser(newStatus, user.userType);
        confirmActive();
      },
    });
  };

  return (
    <>
      {renderConfirmationModal()}
      <div className="grid grid-cols-6 h-[80px] border-b border-[#C2C2C2]">
        <div className="col-span-2 flex gap-4 items-center">
          <ColoredIcon
            Icon={IconUserFilled}
            iconColor="#297d53"
            iconBackgroundClass="bg-[#cadfd4]"
          />
          <div>
            <p className="text-base font-bold">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-base text-text-secondary">{user.title}</p>
          </div>
        </div>
        <div className="col-span-2 flex items-center">{user.userEmail}</div>
        <div className="col-span-1 flex items-center cursor-pointer">
          <Switch
            labelPosition="left"
            color="#89b89f"
            disabled={isCurrentUser}
            label={USER_TYPE_MAP[user.userType]}
            checked={adminChecked}
            onChange={(event) => {
              setAdminChecked(event.target.checked, () =>
                showPermissionsUpdateModal(
                  getIsAdmin(user) ? UserTypes.member : UserTypes.admin
                )
              );
            }}
          />
        </div>
        <div className="col-span-1 flex items-center cursor-pointer">
          <Switch
            labelPosition="left"
            color="#89b89f"
            label={USER_STATUS_MAP[user.userStatus]}
            checked={activeChecked}
            disabled={isCurrentUser}
            onChange={(event) => {
              setActiveChecked(event.target.checked, () =>
                showAccountUpdateModal(
                  getIsActive(user) ? UserStatus.inactive : UserStatus.active
                )
              );
            }}
          />
        </div>
      </div>
    </>
  );
};

const PlaformUsers = ({ currentUser }: { currentUser: User }) => {
  const { data: users, isLoading } = useGetUsersQuery();
  const { renderInviteModal, showInviteModal } = useInviteModal();

  if (isLoading || !users) {
    return <Skeleton className="rounded-[2rem]" height={450} />;
  }

  return (
    <div className="bg-white rounded-[2rem] p-8">
      {renderInviteModal()}
      <div className="flex items-center justify-between pb-4">
        <p className="text-xl font-bold pb-4">Platform Users</p>

        <div>
          <Button
            slim
            prefixIcon={<IconUserPlus />}
            onClick={() => showInviteModal()}
            text="Invite User"
            variant="primary"
          />
        </div>
      </div>
      <div className="bg-[#e9f2ee] w-full rounded-[2rem] p-8">
        <div className="grid grid-cols-6 h-[40px] border-b border-[#C2C2C2]">
          <div className="col-span-2 font-bold">Name/Title</div>
          <div className="col-span-2 font-bold">Email</div>
          <div className="col-span-1 font-bold">Permissions</div>
          <div className="col-span-1 font-bold">Account Status</div>
        </div>

        {[...users]
          .sort((a, b) => {
            if (a.firstName < b.firstName) {
              return -1;
            }

            if (a.firstName > b.firstName) {
              return 1;
            }

            return 0;
          })
          .map((user) => (
            <PlatformUser
              user={user}
              isCurrentUser={user.userId === currentUser.userId}
            />
          ))}
      </div>
    </div>
  );
};

export const ProfilePage = () => {
  const { data: user, isLoading } = useGetUserQuery();
  const loading = !user || isLoading;

  const isAdminUser = () => {
    if (!user) {
      return false;
    }
    return user.userType === UserTypes.admin;
  };

  const profile = () => {
    return <UserInfo />;
  };

  const admin = () => {
    if (!user) {
      return null;
    }

    return <PlaformUsers currentUser={user} />;
  };

  return (
    <div>
      <div className="pb-8">
        {loading ? (
          <Skeleton width={150} height={30} className="rounded-md" />
        ) : (
          <span className="text-2xl font-bold">
            {isAdminUser() ? 'My Account' : 'My Profile'}
          </span>
        )}
      </div>

      <Tabs
        searchParamName="pt"
        tabs={[
          { title: 'Profile Settings', render: profile },
          ...(isAdminUser()
            ? [{ title: 'Admin Settings', render: admin }]
            : []),
        ]}
      />
    </div>
  );
};
